import React from 'react';
import { Box, Typography } from '@mui/material';
import StatusChip from '../../../shared/components/StatusChip';
import { safeFormatAge, utcTitle } from '../../../shared/utils/dateUtils';
import { BaselineStackStatus, type BaselineStackView } from '../../../types/backend';

const NOT_RECORDED = 'not recorded yet';

interface FactProps {
  label: string;
  children: React.ReactNode;
  title?: string;
}

const Fact: React.FC<FactProps> = ({ label, children, title }) => (
  <Typography variant="caption" color="text.secondary" component="div">
    {label}{' '}
    <Box component="span" title={title} sx={{ color: 'text.primary', wordBreak: 'break-all' }}>
      {children}
    </Box>
  </Typography>
);

const When: React.FC<{ label: string; at?: string | null }> = ({ label, at }) => (
  <Fact label={label} title={utcTitle(at)}>
    {at ? safeFormatAge(at, NOT_RECORDED) : NOT_RECORDED}
  </Fact>
);

const StackRow: React.FC<{ stack: BaselineStackView }> = ({ stack }) => {
  const refusals = stack.refusals ?? [];
  // A converged stack's change set was the empty one, reaped the same tick;
  // its name only says something while a set is in play or stopped.
  const showChangeSet = Boolean(stack.change_set_name) && stack.status !== BaselineStackStatus.CONVERGED;
  return (
    <Box
      component="li"
      aria-label={`${stack.stack_name} in ${stack.region}`}
      sx={{ listStyle: 'none', borderTop: 1, borderColor: 'divider', pt: 0.75 }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
          {stack.stack_name}
          <Box component="span" sx={{ color: 'text.secondary' }}>{` · ${stack.region}`}</Box>
        </Typography>
        <StatusChip status={stack.status} />
      </Box>
      {stack.summary && (
        <Typography variant="caption" component="div" sx={{ mt: 0.25 }}>
          {stack.summary}
        </Typography>
      )}
      <Box
        sx={{
          mt: 0.5,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
          columnGap: 2,
        }}
      >
        <When label="Installed" at={stack.stack_created_at} />
        <When label="Last changed" at={stack.stack_last_changed_at} />
        <When label="Last checked" at={stack.observed_at} />
        <Fact label="Template">{stack.applied_hash ? stack.applied_hash.slice(0, 8) : NOT_RECORDED}</Fact>
        <Fact label="Credential">{stack.credential_used || NOT_RECORDED}</Fact>
        {showChangeSet && <Fact label="Change set">{stack.change_set_name}</Fact>}
      </Box>
      {refusals.length > 0 && (
        <Box component="ul" aria-label="What stopped it" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {refusals.map((r) => (
            <Typography key={r} component="li" variant="caption" color="warning.main">{r}</Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export interface BaselineStackDetailsProps {
  /** The baseline's title, which names the list. */
  title: string;
  stacks: BaselineStackView[];
}

/** What "in sync" means for one baseline: per stack and region, what is installed, where and when. */
const BaselineStackDetails: React.FC<BaselineStackDetailsProps> = ({ title, stacks }) => (
  <Box
    component="ul"
    aria-label={`${title} stacks`}
    sx={{ m: 0, mt: 0.75, p: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}
  >
    {stacks.map((s) => <StackRow key={`${s.component_name}@${s.region}`} stack={s} />)}
  </Box>
);

export default BaselineStackDetails;
